// js/straton.js
class ImageGalleryScrolling extends HTMLElement {
  constructor() {
    super();
    this.currentAnimation = null;
    this.isInitialized = false;
    
    // Initialiser quand l'élément est visible
    this.initializeWhenVisible();
    
    // Gérer la pause au hover si l'attribut est présent
    if (this.hasAttribute("pause-on-hover")) {
      this.addEventListener("pointerenter", () => this.currentAnimation?.pause());
      this.addEventListener("pointerleave", () => this.currentAnimation?.play());
    }
  }

  initializeWhenVisible() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isInitialized) {
          this.initialize();
          observer.unobserve(this);
        }
      });
    }, { 
      rootMargin: '400px' 
    });
    
    observer.observe(this);
  }

  get direction() {
    return this.getAttribute("direction") === "right" ? 1 : -1;
  }

  get speed() {
    return parseFloat(this.getAttribute("speed")) || 0.5;
  }

  get track() {
    return this.querySelector('.image-gallery-track');
  }

  initialize() {
    if (this.isInitialized || !this.track || this.track.children.length === 0) {
      return;
    }

    const track = this.track;
    
    // Mesurer la largeur naturelle du contenu
    const naturalWidth = this.measureTrackWidth();
    
    // Dupliquer le contenu pour l'effet infini
    const originalContent = track.innerHTML;
    track.innerHTML = originalContent + originalContent;
    
    // Définir la largeur du track pour qu'elle corresponde exactement au contenu
    track.style.width = (naturalWidth * 2) + 'px';
    
    // Démarrer l'animation
    this.startAnimation(naturalWidth);
    this.isInitialized = true;
  }

  measureTrackWidth() {
    const track = this.track;
    let totalWidth = 0;
    
    // Calculer la largeur totale des éléments + gaps
    Array.from(track.children).forEach((child, index) => {
      totalWidth += child.getBoundingClientRect().width;
      
      // Ajouter le gap sauf pour le dernier élément
      if (index < track.children.length - 1) {
        const gap = parseInt(getComputedStyle(track).gap) || 0;
        totalWidth += gap;
      }
    });
    
    return totalWidth;
  }

  startAnimation(naturalWidth) {
    const track = this.track;
    if (!track || !naturalWidth) return;

    // Calculer la durée (plus la vitesse est élevée, moins c'est long)
    const duration = (naturalWidth / 100) * (1 / this.speed);
    
    // Définir les keyframes selon la direction
    const startPos = this.direction === -1 ? 0 : -naturalWidth;
    const endPos = this.direction === -1 ? -naturalWidth : 0;
    
    const keyframes = [
      { transform: `translateX(${startPos}px)` },
      { transform: `translateX(${endPos}px)` }
    ];

    // Créer l'animation
    this.currentAnimation = track.animate(keyframes, {
      duration: duration * 1000,
      easing: 'linear',
      iterations: Infinity
    });
  }

  // Méthodes publiques pour contrôler l'animation
  pause() {
    this.currentAnimation?.pause();
  }

  play() {
    this.currentAnimation?.play();
  }

  stop() {
    this.currentAnimation?.cancel();
  }

  // Observer les changements d'attributs
  static get observedAttributes() {
    return ['speed', 'direction'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (this.isInitialized && oldValue !== newValue) {
      // Redémarrer l'animation avec les nouveaux paramètres
      this.stop();
      const naturalWidth = this.measureTrackWidth() / 2; // Divisé par 2 car dupliqué
      this.startAnimation(naturalWidth);
    }
  }

  // Cleanup quand l'élément est retiré du DOM
  disconnectedCallback() {
    this.currentAnimation?.cancel();
  }
}

// Enregistrer le custom element
if (!window.customElements.get("image-gallery-scrolling")) {
  window.customElements.define("image-gallery-scrolling", ImageGalleryScrolling);
}